import { useEffect, useState } from 'react';
import useStoreFilter from './useStoreFilter';
import { Alert, message } from 'antd';
import ShopeePerformanceTable from './ShopeePerformanceTable';
import { useRequest } from 'ahooks';
import { getStorePerfList } from './service';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import useModal from 'hooks/useModal';
import ShopeeTrendDrawer from './ShopeeTrendDrawer';
import { eventBus } from 'services/eventBus';
import dayjs from 'dayjs';

function ShopeePerformance({ departments }: { departments: Dashboard.Department[] }) {
  const { t } = useTranslate();

  const {
    selectedDepartments,
    departmentsSelect,
    storeType,
    storeTypeSelect,
    storeInfo,
    storeInfoSelect,
  } = useStoreFilter({
    platform: 'SHOPEE',
    departments,
  });

  const filter = (
    <header>
      {departmentsSelect}
      {storeTypeSelect}
      {storeInfoSelect}
    </header>
  );

  const dateRender = (x: number) => dayjs().subtract(x, 'days').format('YYYY/MM/DD');

  const banner = (
    <Alert
      banner
      showIcon
      type="info"
      message={
        <div>
          <div>
            {t('店铺评价与聊天数据统计区间：x-y', {
              x: dateRender(31),
              y: dateRender(2),
            })}
          </div>
          <div>
            {t('订单数据统计区间：x-y', {
              x: dateRender(8),
              y: dateRender(2),
            })}
          </div>
          <div>
            {t(
              '聊天满意度目前接口仅开放了印尼、马来西亚、新加坡站点；首次/平均响应时长目前接口仅开放了马来西亚、菲律宾、新加坡、泰国站点。',
            )}
          </div>
        </div>
      }
    />
  );

  const checkDetail = ({
    storeId,
    region,
    storeName,
    storeAlias,
  }: Dashboard.PlatformData.StorePerfData) => {
    eventBus.emit('CHECK_STORE_PLATFORM_CSAT_DETAIL', {
      id: storeId as any,
      platform: 'SHOPEE',
      region,
      storeName,
      storeNameAlias: storeAlias,
      storeType: 'LOCAL',
    });
  };

  const { visible, show, hide, meta } = useModal<Dashboard.PlatformData.StorePerfData>();

  const drawer = meta && (
    <ShopeeTrendDrawer
      key={meta.storeId}
      visible={visible}
      onClose={() => hide()}
      storePerfData={meta}
      onCheckDetail={checkDetail}
    />
  );

  const [sortKey, setSortKey] = useState<string>();
  const [sortType, setSortType] = useState<1 | 2>();

  const { loading, data, run } = useRequest(
    (
      page: {
        pageNum: number;
        pageSize: number;
      } = {
        pageNum: 1,
        pageSize: 10,
      },
    ) => {
      return getStorePerfList({
        ...page,
        sortKey,
        sortType,
        platform: 'SHOPEE',
        departmentIds: selectedDepartments.map(({ id }) => id),
        storeType: storeType === 'CROSS' ? '跨境' : storeType === 'LOCAL' ? '本土' : undefined,
        storeIds: storeInfo.list?.map(({ id }) => Number(id)),
      });
    },
    {
      manual: true,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  useEffect(() => {
    run();
  }, [run, sortKey, sortType, selectedDepartments, storeType, storeInfo]);

  const table = (
    <ShopeePerformanceTable
      loading={loading}
      dataSource={data?.list}
      pagination={{
        current: data?.pageNum || 1,
        pageSize: data?.pageSize || 10,
        total: data?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (x) => t('feedback.共x项', { x }),
      }}
      onChange={(page: any, _, { order, field }: any) => {
        if (page.current === (data?.pageNum || 1) && page.pageSize === (data?.pageSize || 10)) {
          setSortKey(order && field);
          setSortType(order === 'ascend' ? 1 : order === 'descend' ? 2 : undefined);
        } else {
          run({
            pageNum: page.current,
            pageSize: page.pageSize,
          });
        }
      }}
      onCheckDetail={checkDetail}
      onCheckTrend={show}
    />
  );

  return (
    <Container>
      {filter}
      {banner}
      <section>{table}</section>
      {drawer}
    </Container>
  );
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    padding: 8px 16px;
    margin-bottom: 16px;
    margin-top: 1px;
    background: #fff;
    > * {
      margin-right: 16px;
    }
  }
  > section {
    flex: 1 0 0;
    padding: 16px;
    background: #fff;
  }
`;

export default ShopeePerformance;
